import {
	COMPANY_MANAGED_PROJECT,
	TEAM_MANAGED_PROJECT,
	type ProjectType,
	type JiraProjectStyle,
} from '@jira/platform__project-types/src/project-types.tsx';
import type {
	ProjectContext,
	ProjectIssueType as IssueType,
} from './ProjectContextPayload.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type Store,
} from '@atlassian/react-sweet-state';
import * as actions from './mergeProjectProperty.tsx';
import type { State, SelectorProps } from './ProjectContextServiceActions.types.tsx';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const initialState: Record<string, any> = {};
type Actions = typeof actions;

export const projectContextStore: Store<State, typeof actions> = createStore<State, Actions>({
	name: 'project-context',
	initialState,
	actions,
});

export const useProjectContextActionStore: HookFunction<
	State,
	BoundActions<State, typeof actions>,
	void
> = createHook(projectContextStore, {
	selector: null,
});

const getProjectContext = (
	state: State,
	{ projectKey }: SelectorProps,
): ProjectContext | undefined => state[projectKey];
export const useProjectContextStore: HookFunction<
	ProjectContext | undefined,
	BoundActions<State, typeof actions>,
	SelectorProps
> = createHook(projectContextStore, {
	selector: getProjectContext,
});

const getProjectType = (state: State, { projectKey }: SelectorProps): ProjectType | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.projectType;
};

const getJiraProjectStyle = (
	state: State,
	{ projectKey }: SelectorProps,
): JiraProjectStyle | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.isSimplified ? TEAM_MANAGED_PROJECT : COMPANY_MANAGED_PROJECT;
};

export const useProjectContextProjectTypeStore: HookFunction<
	ProjectType | undefined,
	BoundActions<State, typeof actions>,
	SelectorProps
> = createHook(projectContextStore, {
	selector: getProjectType,
});

export const useProjectContextJiraProjectStyleStore: HookFunction<
	JiraProjectStyle | undefined,
	BoundActions<State, typeof actions>,
	SelectorProps
> = createHook(projectContextStore, {
	selector: getJiraProjectStyle,
});

const getProjectName = (state: State, { projectKey }: SelectorProps): string | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.projectName;
};

export const useProjectContextProjectNameStore: HookFunction<
	string | undefined,
	BoundActions<State, typeof actions>,
	SelectorProps
> = createHook(projectContextStore, {
	selector: getProjectName,
});

const getIssueTypes = (state: State, { projectKey }: SelectorProps): IssueType[] | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.issueTypes;
};

export const useProjectContextIssueTypesStore: HookFunction<
	IssueType[] | undefined,
	BoundActions<State, typeof actions>,
	SelectorProps
> = createHook(projectContextStore, {
	selector: getIssueTypes,
});
